import React, { useEffect, useState } from 'react';
import { Lock, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { buildPrefilledCheckoutUrl } from '@/lib/checkout';
import { buildLeadPayload, formatPhone, getTrackingParams, sendLead } from '@/lib/leadTracking';

export type CheckoutPlan = {
  id: string;
  name: string;
  price: string;
  checkoutUrl: string;
};

interface LeadCheckoutDialogProps {
  plan: CheckoutPlan | null;
  onOpenChange: (open: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const inputClass =
  'w-full rounded-xl border border-gray-300 bg-white px-4 py-3.5 text-base text-gray-900 caret-[#8e3a87] placeholder:text-gray-500 outline-none transition-[border-color,box-shadow] duration-150 focus:border-[#8e3a87] focus:ring-4 focus:ring-[#8e3a87]/15 aria-[invalid=true]:border-red-400';

const LeadCheckoutDialog: React.FC<LeadCheckoutDialogProps> = ({ plan, onOpenChange }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (plan) {
      setError('');
      setSubmitting(false);
    }
  }, [plan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan || submitting) return;

    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();
    const phoneDigits = phone.replace(/\D/g, '');

    if (cleanName.length < 2) return setError('Digite seu nome.');
    if (!EMAIL_RE.test(cleanEmail)) return setError('Digite um e-mail válido.');
    if (phoneDigits.length < 10) return setError('Digite seu WhatsApp com DDD.');

    setError('');
    setSubmitting(true);

    const lead = { name: cleanName, email: cleanEmail, phone: phoneDigits };
    const checkoutUrl = buildPrefilledCheckoutUrl(plan.checkoutUrl, getTrackingParams(), lead);

    const payload = await buildLeadPayload({
      ...lead,
      product_id: plan.id,
      product_name: plan.name,
      product_price: plan.price,
      checkout_url: checkoutUrl,
    });

    try {
      (window as any).fbq?.('track', 'Lead', { content_name: plan.name });
      (window as any).fbq?.('track', 'InitiateCheckout', { content_name: plan.name });
    } catch {
      // pixel indisponível
    }

    // Espera o envio por no máximo 2s; o keepalive segura o restante durante o redirect
    await Promise.race([sendLead(payload), new Promise((r) => setTimeout(r, 2000))]);
    window.location.href = checkoutUrl;
  };

  return (
    <Dialog open={!!plan} onOpenChange={(open) => !submitting && onOpenChange(open)}>
      <DialogContent className="max-w-md overflow-hidden rounded-3xl border-none p-0 sm:rounded-3xl">
        <div className="bg-gradient-to-br from-[#2a1744] via-[#4a3371] to-[#5f448f] px-6 pb-6 pt-7 text-white">
          <DialogHeader className="space-y-2 text-left">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white/90">
              <Lock className="h-3 w-3" /> Último passo
            </span>
            <DialogTitle className="text-2xl font-extrabold leading-tight text-white">{plan?.name}</DialogTitle>
            <DialogDescription className="text-sm text-white/75">
              Preencha seus dados para seguir ao pagamento seguro de <strong className="text-white">{plan?.price}</strong>.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 bg-white px-6 pb-6 pt-5" noValidate>
          <div className="space-y-1.5">
            <label htmlFor="lead-name" className="text-sm font-semibold text-gray-700">Nome completo</label>
            <input id="lead-name" className={inputClass} autoComplete="name" placeholder="Seu nome" required aria-invalid={error === 'Digite seu nome.'} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="lead-email" className="text-sm font-semibold text-gray-700">E-mail</label>
            <input id="lead-email" type="email" inputMode="email" className={inputClass} autoComplete="email" placeholder="seuemail@exemplo.com" required aria-invalid={error === 'Digite um e-mail válido.'} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="lead-phone" className="text-sm font-semibold text-gray-700">WhatsApp</label>
            <input id="lead-phone" type="tel" inputMode="tel" className={inputClass} autoComplete="tel-national" placeholder="(51) 99999-9999" required aria-invalid={error === 'Digite seu WhatsApp com DDD.'} value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
          </div>

          {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="group flex w-full items-center justify-center gap-2 rounded-2xl bg-[#a54d9e] px-6 py-4 text-lg font-extrabold text-white shadow-[0_8px_20px_rgba(42,23,68,0.2)] transition-[background-color,transform] duration-200 ease-out hover:bg-[#8e3a87] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#a54d9e]/30 disabled:cursor-wait disabled:opacity-90"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Indo para o pagamento...
              </>
            ) : (
              <>
                Ir para o pagamento <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
              </>
            )}
          </button>

          <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
            <ShieldCheck className="h-4 w-4 text-[#1f7f81]" aria-hidden="true" /> Seus dados estão seguros · Garantia de 7 dias
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCheckoutDialog;
